import { expandBody } from "./blogExpansion";
import { scheduledPart1 } from "./scheduled/part-1";
import { scheduledPart2 } from "./scheduled/part-2";
import { scheduledPart3 } from "./scheduled/part-3";
import { scheduledPart4 } from "./scheduled/part-4";
import { scheduledPart5 } from "./scheduled/part-5";
import { scheduledPart6 } from "./scheduled/part-6";
import { backfillPart1 } from "./backfill/part-1";
import { backfillPart2 } from "./backfill/part-2";
import { backfillPart3 } from "./backfill/part-3";
import { backfillPart4 } from "./backfill/part-4";
import { backfillPart5 } from "./backfill/part-5";
import { backfillPart6 } from "./backfill/part-6";

export interface BlogSection {
  type: 'paragraph' | 'heading' | 'bullet-list' | 'faq';
  content?: string;
  items?: string[];
  qa?: { q: string; a: string }[];
}

export interface BlogPost {
  slug: string;
  title: string;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string[];
  excerpt: string;
  category: string;
  publishedDate: string;
  heroImage: string;
  body: BlogSection[];
  author?: string;
}

export const DEFAULT_AUTHOR = "Steven Laureys";

const featured: BlogPost[] = [
  {
    slug: "ai-visibility-strategy-outside-english",
    title: "Your AI Visibility Strategy Doesn't Work Outside English",
    seoTitle: "Multilingual AI Visibility for Dealerships: Why English-Only GEO Fails",
    seoDescription: "GEO and AEO break down outside English. Here's how dealerships and OEMs win AI visibility in Spanish, Vietnamese, and other multilingual markets.",
    keywords: ["multilingual SEO automotive", "AI visibility dealership", "GEO non-English", "AEO multilingual", "automotive answer engine optimization"],
    excerpt: "Why relying solely on English for Generative Engine Optimization (GEO) and Answer Engine Optimization (AEO) leaves you invisible to diverse markets.",
    category: "AI Visibility",
    publishedDate: "2023-11-15",
    heroImage: "/images/blog-ai-visibility.jpg",
    body: [
      { type: "heading", content: "The Multilingual Gap in AI Search" },
      { type: "paragraph", content: "Most automotive marketing teams are just waking up to AEO (Answer Engine Optimization) and GEO (Generative Engine Optimization). They are optimizing their inventory and content so that when a user asks ChatGPT or Perplexity, \"What's the best mid-size SUV under $40k near me?\" their dealership shows up. But they are doing it exclusively in English. If you operate in a diverse market, your English-only vector index strategy is broken." },
      { type: "heading", content: "Vector Index Hygiene Across Languages" },
      { type: "paragraph", content: "LLMs don't keyword match; they retrieve context via vector embeddings. If your Spanish or Vietnamese content is a poorly translated afterthought—or worse, dynamically translated via a client-side widget—the LLM cannot ingest it properly during its training or retrieval phase." },
      { type: "bullet-list", items: [
        "Dynamic translation widgets are invisible to LLM crawlers.",
        "Literal translations lose semantic meaning and colloquial search intent.",
        "Entity disambiguation fails when proper nouns are inconsistently localized."
      ]},
      { type: "heading", content: "Fixing Multilingual Entity Disambiguation" },
      { type: "paragraph", content: "To fix this, you must construct a multilingual knowledge graph. Ensure your structured data (JSON-LD) clearly links entities across languages. If you want to dominate diverse markets, your content must be natively structured for the LLMs in every language your buyers speak." }
    ]
  },
  {
    slug: "google-product-feed-strategy-future-retail",
    title: "Google's Product Feed Strategy Points To The Future Of Retail Discovery",
    seoTitle: "Google Product Feeds: The Future of Automotive Retail Discovery",
    seoDescription: "Vehicle inventory feeds now drive organic, image, and AI-generated discovery on Google. Here's what dealers and OEMs need to know.",
    keywords: ["Google vehicle ads", "automotive product feeds", "Shopping Graph dealership", "VIN-level SEO", "automotive Merchant Center"],
    excerpt: "How inventory feeds are moving beyond Google Shopping into the core of retail discovery.",
    category: "SEO",
    publishedDate: "2023-10-22",
    heroImage: "/images/blog-product-feed.jpg",
    body: [
      { type: "heading", content: "Beyond Google Shopping" },
      { type: "paragraph", content: "For years, automotive product feeds were relegated to Google Vehicle Ads or standard Shopping campaigns. But Google's core discovery mechanisms are changing. The 'Shopping Graph' now influences organic search, image search, and generative AI answers." },
      { type: "heading", content: "Structured Data is the New SEO" },
      { type: "paragraph", content: "If your inventory isn't properly structured with deep attributes (trim, packages, transit status), you are invisible to high-intent queries. Traditional SEO focused on dealer-level queries (\"Ford dealer near me\"). The future of retail discovery focuses on VIN-level queries (\"Blue Ford Bronco Badlands soft top near me\")." },
      { type: "bullet-list", items: [
        "Implement real-time feed synchronization.",
        "Enrich feeds with custom attributes beyond the OEM defaults.",
        "Ensure Merchant Center health is monitored daily."
      ]},
      { type: "paragraph", content: "Relevant Dealer builds data architectures that ensure your inventory data is the cleanest, most accessible data in your market." }
    ]
  },
  {
    slug: "7-ways-businesses-use-drone-video-2026",
    title: "7 Ways Businesses Are Using Drone Video in 2026",
    seoTitle: "7 Ways Car Dealerships Are Using Drone Video in 2026",
    seoDescription: "From inventory walkarounds to service lane flyovers, see seven drone video tactics car dealerships are using in 2026 to stop the scroll and sell.",
    keywords: ["drone video for dealerships", "automotive drone marketing 2026", "dealership flyover video", "inventory walkaround drone", "drone video automotive"],
    excerpt: "From inventory walkarounds to service lane marketing, drone video is changing automotive content.",
    category: "Drone Video",
    publishedDate: "2023-12-05",
    heroImage: "/images/blog-drone-1.jpg",
    body: [
      { type: "heading", content: "The Evolution of Automotive Video" },
      { type: "paragraph", content: "The shaky iPhone walkaround is dead. By 2026, high-production drone and stabilized video will be the baseline expectation for automotive consumers. Here are 7 ways the best operators are using drones today." },
      { type: "bullet-list", items: [
        "Cinematic facility flyovers for brand campaigns.",
        "Dynamic inventory walkarounds tracking moving vehicles.",
        "Service lane operations highlighted from above.",
        "Community involvement and event coverage.",
        "Fixed-ops transparency (showing the scale of the parts department).",
        "Social media shorts designed for TikTok and Reels.",
        "Recruiting videos showing the true scale of the operation."
      ]},
      { type: "heading", content: "Integrating Video into the Funnel" },
      { type: "paragraph", content: "Don't just shoot a video and leave it on YouTube. We integrate drone assets into VDPs, Meta ads, and automated email sequences to maximize ROI on production costs." }
    ]
  },
  {
    slug: "boost-dealership-sales-drone-video",
    title: "Boost Your Dealership Sales with Drone Video Marketing",
    seoTitle: "Drone Video Marketing for Car Dealerships: A Sales Playbook",
    seoDescription: "A tactical playbook for using drone video to drive dealership sales — Meta retargeting, VDP integration, and showroom anxiety reduction.",
    keywords: ["drone video marketing dealership", "automotive drone advertising", "Meta retargeting dealership video", "VDP video marketing"],
    excerpt: "A tactical guide to turning high-quality drone production into sold units.",
    category: "Drone Video",
    publishedDate: "2023-11-28",
    heroImage: "/images/blog-drone-2.jpg",
    body: [
      { type: "heading", content: "Attention is the Currency" },
      { type: "paragraph", content: "In a feed full of static images, motion wins. Drone video captures attention immediately, but attention doesn't pay the bills—conversions do." },
      { type: "heading", content: "The Playbook" },
      { type: "paragraph", content: "First, use drone flyovers of your lot to create custom audiences on Meta. Retarget users who watch more than 50% of the video with specific inventory ads. Second, use interior drone fly-throughs to showcase the customer experience, lowering the anxiety of the dealership visit." },
      { type: "faq", qa: [
        { q: "Is drone video too expensive for a single-point store?", a: "No. When batched correctly, a single shoot days yields months of social content, ad creative, and website assets." }
      ]}
    ]
  },
  {
    slug: "fixing-ga4-attribution-car-dealerships",
    title: "Fixing Attribution Issues in GA4 for Car Dealerships",
    seoTitle: "Fix GA4 Attribution Issues for Car Dealerships (Step by Step)",
    seoDescription: "Direct traffic eating your conversions? Here's how to fix GA4 attribution for car dealerships — cross-domain, GBP UTMs, session timeouts, and DDA.",
    keywords: ["GA4 attribution car dealerships", "fix GA4 dealership", "automotive GA4 setup", "cross-domain tracking dealership", "data-driven attribution automotive"],
    excerpt: "Stop misattributing sales to direct traffic. How to fix the most common GA4 setup errors in automotive.",
    category: "GA4 & Attribution",
    publishedDate: "2024-01-10",
    heroImage: "/images/blog-ga4-1.jpg",
    body: [
      { type: "heading", content: "The 'Direct Traffic' Lie" },
      { type: "paragraph", content: "If your GA4 shows 'Direct' driving 60% of your leads, your setup is broken. Automotive customer journeys are complex, spanning multiple sessions, devices, and platforms." },
      { type: "heading", content: "Common Pitfalls" },
      { type: "bullet-list", items: [
        "Cross-domain tracking failures between the main site and the digital retailing tool.",
        "Missing UTM parameters on local SEO profiles (Google Business Profile).",
        "Improper session timeout settings for automotive buying cycles."
      ]},
      { type: "paragraph", content: "We audit and rebuild GA4 environments to ensure every dollar of ad spend is properly credited, moving away from last-click models to data-driven attribution." }
    ]
  },
  {
    slug: "track-phone-calls-ga4-right-way",
    title: "How to Track Phone Calls in GA4 for Car Dealerships (The Right Way)",
    seoTitle: "Track Phone Calls in GA4 for Car Dealerships With DNI",
    seoDescription: "How to track phone calls in GA4 for car dealerships using Dynamic Number Insertion, measurement protocol, and Google Ads conversion imports.",
    keywords: ["track phone calls GA4 dealership", "DNI GA4 automotive", "phone call attribution dealership", "measurement protocol DNI", "GA4 phone conversions"],
    excerpt: "Integrating Dynamic Number Insertion (DNI) with GA4 to close the offline conversion gap.",
    category: "GA4 & Attribution",
    publishedDate: "2024-01-22",
    heroImage: "/images/blog-attribution.png",
    body: [
      { type: "heading", content: "The Offline Blind Spot" },
      { type: "paragraph", content: "A customer clicks a paid search ad, browses your inventory, and then picks up the phone to call the store. If you aren't tracking that call back to the specific ad click, you are flying blind." },
      { type: "heading", content: "Implementing DNI" },
      { type: "paragraph", content: "Dynamic Number Insertion (DNI) swaps the phone number on your website based on the traffic source. We pipe this data directly into GA4 via measurement protocol, ensuring phone calls appear as conversion events alongside form submits." },
      { type: "bullet-list", items: [
        "Track calls down to the keyword level.",
        "Filter out short calls and service calls from sales attribution.",
        "Push high-quality call conversions back into Google Ads to train the bidding algorithm."
      ]}
    ]
  },
  {
    slug: "why-most-dealer-seo-programs-stall",
    title: "Why Most Dealer SEO Programs Stall At Month 6",
    seoTitle: "Why Dealer SEO Programs Stall at Month 6 (And How to Fix It)",
    seoDescription: "Most car dealership SEO programs flatline at month six. Learn why template SEO breaks down and what real, ongoing automotive SEO looks like.",
    keywords: ["dealer SEO program", "automotive SEO plateau", "car dealership SEO retainer", "ongoing SEO automotive", "SEO month 6 plateau"],
    excerpt: "The truth about vendor SEO packages and why your organic growth flatlines after the initial technical fixes.",
    category: "SEO",
    publishedDate: "2024-02-15",
    heroImage: "/images/blog-seo.png",
    body: [
      { type: "heading", content: "The Month 6 Plateau" },
      { type: "paragraph", content: "Every dealer knows the cycle: hire a new SEO vendor, see a bump in traffic as they fix broken title tags and site speed, and then watch the growth flatline by month six." },
      { type: "heading", content: "Why It Happens" },
      { type: "paragraph", content: "Template SEO doesn't scale. Once the technical baseline is established, you need real, localized, entity-driven content and digital PR. Most vendors just keep charging a retainer to run automated reports." },
      { type: "paragraph", content: "Relevant Dealer breaks the plateau by treating SEO as a continuous operational function, integrating inventory data, local partnerships, and advanced entity optimization that template providers cannot execute." }
    ]
  },
  {
    slug: "oem-co-op-marketing-leaving-money-table",
    title: "OEM Co-op Marketing: How To Stop Leaving Money On The Table",
    seoTitle: "OEM Co-op Marketing: Claim More Eligible Funds",
    seoDescription: "Leaving eligible OEM co-op funds unclaimed? Learn how to organize approvals, campaign requirements, and proof of performance for dealership reimbursement.",
    keywords: ["OEM co-op marketing", "dealer co-op reimbursement", "automotive co-op compliance", "tier 2 marketing dealership", "OEM co-op funds"],
    excerpt: "Maximize your co-op reimbursement without sacrificing your local brand identity.",
    category: "Dealer Marketing",
    publishedDate: "2024-03-01",
    heroImage: "/images/blog-oem.jpg",
    body: [
      { type: "heading", content: "The Co-op Dilemma" },
      { type: "paragraph", content: "OEM co-op funds are essentially free money, yet millions go unclaimed every year because the compliance rules are too burdensome, or dealers don't want to run generic tier-2 creative." },
      { type: "heading", content: "Orchestrating Compliance" },
      { type: "paragraph", content: "As your fractional CMO, we navigate the co-op guidelines for you. We design custom campaigns that maintain your unique store identity while satisfying the OEM requirements for full reimbursement." },
      { type: "bullet-list", items: [
        "Pre-approval routing processes.",
        "Automated proof-of-performance reporting.",
        "Strategic allocation to maximize match rates."
      ]}
    ]
  }
];

interface Angle {
  s: string;
  t: string;
  st: string;
  sd: string;
  ex: string;
  k: string[];
  body: BlogSection[];
}

interface Topic {
  name: string;
  img: string | string[];
  angles: Angle[];
}

const topics: Topic[] = [
  {
    name: "AI Visibility",
    img: "/images/blog-ai-visibility.jpg",
    angles: [
      {
        s: "knowledge-graph-ai-auto-search",
        t: "Building a Knowledge Graph for AI-Powered Auto Search",
        st: "Knowledge Graph SEO for Car Dealerships and OEMs",
        sd: "How automotive marketers build entity-rich knowledge graphs that ChatGPT, Perplexity, and Google AI Overviews actually cite.",
        ex: "Entities, not keywords, drive AI search. Here's how to build a dealer-grade knowledge graph that LLMs cite.",
        k: ["automotive knowledge graph", "entity SEO dealership", "AI Overviews citations", "schema for car dealers"],
        body: [
          { type: "heading", content: "From Keywords to Entities" },
          { type: "paragraph", content: "Generative engines do not pick winners by keyword density. They pick the entity they trust most for a given intent. For dealerships and OEMs, that means your store, your inventory, your people, and your services all need to exist as well-connected entities in the public graph." },
          { type: "heading", content: "Connecting Your Entities" },
          { type: "paragraph", content: "Start with a clean Organization schema linked via @id, then connect every Vehicle, Service, and Person entity back to it. Cross-reference Google Business Profile, Wikidata where possible, and OEM brand pages. Consistency is the moat." },
          { type: "bullet-list", items: [
            "Use stable @id URIs across all JSON-LD blocks.",
            "Link Vehicle entities to AutoDealer via offers and isAvailableAt.",
            "Reference Person entities for sales, service, and ownership consistently."
          ]},
          { type: "paragraph", content: "Knowledge graph work compounds. The earlier you start, the more durable your AI visibility becomes." }
        ]
      },
      {
        s: "how-llms-read-dealership-website",
        t: "How LLMs Read Your Dealership Website (And Why It Matters)",
        st: "How AI Models Crawl Dealership Sites — A Practical Breakdown",
        sd: "A practical look at how LLM crawlers ingest dealership websites — and the technical mistakes that make your inventory invisible to AI.",
        ex: "If GPTBot, ClaudeBot, and PerplexityBot can't render your VDPs, you don't exist in AI search.",
        k: ["LLM crawlers", "GPTBot dealership", "AI visibility automotive", "vector embeddings dealer"],
        body: [
          { type: "heading", content: "Crawl, Render, Embed" },
          { type: "paragraph", content: "AI crawlers have stricter rendering budgets than Googlebot. Heavy client-side widgets, lazy-loaded inventory, and obfuscated pricing all reduce your odds of being embedded into a model's retrieval index." },
          { type: "heading", content: "What to Audit Today" },
          { type: "bullet-list", items: [
            "Server-render the critical content of every VDP and SRP.",
            "Allow GPTBot, ClaudeBot, PerplexityBot, and Google-Extended in robots.txt.",
            "Avoid pricing in images — render it as text with structured data."
          ]},
          { type: "paragraph", content: "Treat AI crawl access as a first-class channel. The dealers who do are the ones being cited when buyers ask the assistant for help." }
        ]
      },
      {
        s: "aeo-audit-dealership-chatgpt",
        t: "The AEO Audit: Are You Visible in ChatGPT Auto Queries?",
        st: "Answer Engine Optimization Audit for Car Dealerships",
        sd: "A repeatable AEO audit framework for dealerships — query sets, citation tracking, and remediation playbooks.",
        ex: "Stop guessing whether you're cited by ChatGPT. Here's the dealer AEO audit framework we run for every client.",
        k: ["answer engine optimization", "AEO audit dealership", "ChatGPT visibility automotive", "AI citation tracking"],
        body: [
          { type: "heading", content: "Define the Query Set" },
          { type: "paragraph", content: "AEO starts with a representative query set: shopping queries, comparison queries, service queries, and trust queries. Run them across ChatGPT, Perplexity, Gemini, and Claude with location context. Log who gets cited." },
          { type: "heading", content: "Score, Diagnose, Remediate" },
          { type: "bullet-list", items: [
            "Score visibility per query, per engine, per intent.",
            "Diagnose gaps — schema, content, third-party mentions, or freshness.",
            "Remediate the highest-volume intents first, then re-test monthly."
          ]},
          { type: "paragraph", content: "AEO is measurable when you treat it like SEO ranking tracking. The framework above is the same one we run for OEM and dealer-group clients." }
        ]
      },
      {
        s: "schema-generative-search-vehicle-faq",
        t: "Schema for Generative Search: Vehicle, FAQ, and LocalBusiness",
        st: "Schema Markup That Works for AI Overviews and Dealer Sites",
        sd: "The exact JSON-LD types and patterns that earn dealership citations in AI Overviews and answer engines.",
        ex: "Not all schema is created equal. These are the types that move the needle for AI visibility in automotive.",
        k: ["JSON-LD dealership", "Vehicle schema", "FAQ schema automotive", "AutoDealer schema"],
        body: [
          { type: "heading", content: "The Three Schemas That Matter Most" },
          { type: "paragraph", content: "For a franchise dealer, the highest-leverage schemas are AutoDealer (with full geo and contact details), Vehicle (per VIN with price, mileage, and condition), and FAQPage on every service and pricing page." },
          { type: "heading", content: "Implementation Notes" },
          { type: "bullet-list", items: [
            "Keep one canonical Organization with @id reused across pages.",
            "Render schema server-side so it's visible to LLM crawlers.",
            "Validate with Google's Rich Results test and Schema.org validator."
          ]},
          { type: "paragraph", content: "Schema is not a magic wand, but it is the single biggest unlock for being correctly understood by both Google and the answer engines." }
        ]
      }
    ]
  },
  {
    name: "SEO",
    img: "/images/blog-seo.png",
    angles: [
      {
        s: "dealership-seo-vdp-srp-optimization",
        t: "Dealership SEO Beyond the Homepage: VDP and SRP Optimization",
        st: "Dealer SEO: Optimizing VDPs and SRPs for Real Traffic",
        sd: "Most dealer SEO ignores the highest-converting pages. Here's how to win on VDPs and SRPs — the pages buyers actually land on.",
        ex: "Your homepage is not the page that sells cars. Make your VDPs and SRPs the SEO priority.",
        k: ["VDP SEO", "SRP SEO", "dealership SEO", "automotive on-page SEO"],
        body: [
          { type: "heading", content: "Where Buyers Actually Land" },
          { type: "paragraph", content: "Most organic shopping traffic to a dealer site lands on Search Results Pages and Vehicle Detail Pages, not the homepage. If your SEO program is only optimizing the homepage and a few service pages, you are leaving 70% of the opportunity on the table." },
          { type: "heading", content: "The VDP Optimization Stack" },
          { type: "bullet-list", items: [
            "Server-rendered title and meta tied to year/make/model/trim/VIN.",
            "Vehicle JSON-LD with offers, mileage, and image.",
            "Internal links from related-VDP modules and SRP filters.",
            "Crawl budget routing via XML sitemaps split by inventory state."
          ]},
          { type: "paragraph", content: "Treat your inventory pages as the product pages they are. Every percentage point of organic uplift on VDPs is a measurable lift in store traffic." }
        ]
      },
      {
        s: "local-seo-multi-rooftop-dealer-groups",
        t: "Local SEO for Multi-Rooftop Dealer Groups",
        st: "Local SEO Playbook for Dealer Groups with Many Rooftops",
        sd: "How dealer groups manage GBP, citations, and on-site geo content across multiple rooftops without cannibalizing themselves.",
        ex: "Multi-rooftop groups face a unique local SEO challenge — these are the patterns that scale.",
        k: ["local SEO dealer group", "Google Business Profile dealership", "multi-location SEO automotive", "dealer group citations"],
        body: [
          { type: "heading", content: "The Cannibalization Trap" },
          { type: "paragraph", content: "Two rooftops in the same DMA can quietly steal each other's traffic if they share content templates and identical service descriptions. Differentiation has to be intentional." },
          { type: "heading", content: "Group-Level Patterns" },
          { type: "bullet-list", items: [
            "Per-rooftop Google Business Profile with unique phone, hours, and photos.",
            "Distinct local landing pages with neighborhood-specific copy and reviews.",
            "Centralized review response operations to keep ratings high.",
            "Schema with proper @id per rooftop and parentOrganization linking."
          ]},
          { type: "paragraph", content: "Local SEO at scale is an operations problem first, a content problem second. The groups that win build it as a process, not a one-time project." }
        ]
      },
      {
        s: "technical-seo-audit-checklist-dealer",
        t: "Technical SEO Audit Checklist for Auto Dealer Websites",
        st: "Technical SEO Audit Checklist for Car Dealerships",
        sd: "A no-fluff technical SEO checklist for dealer sites — Core Web Vitals, crawl, indexability, and inventory pipeline checks.",
        ex: "Run this technical SEO checklist on any dealer website before spending another dollar on content.",
        k: ["technical SEO dealer", "Core Web Vitals automotive", "crawl budget dealership", "indexability VDP"],
        body: [
          { type: "heading", content: "What Vendors Quietly Skip" },
          { type: "paragraph", content: "Most vendor 'audits' are visual reports built to upsell content packages. A real audit puts hands on the crawl, the rendered DOM, and the indexability of every VDP." },
          { type: "heading", content: "The Checklist" },
          { type: "bullet-list", items: [
            "Render-blocking JS removed from above-the-fold critical path.",
            "All VDPs reachable within 3 clicks from a sitemap or category hub.",
            "No-index applied to sold inventory but kept indexable until VIN flips.",
            "Hreflang and canonical correct on duplicate inventory feeds.",
            "Image LCP under 2.5s on the median 4G connection."
          ]},
          { type: "paragraph", content: "Run this list quarterly. The gap between a fast, well-indexed site and a slow, half-crawled one is sales-floor traffic, not vanity metrics." }
        ]
      },
      {
        s: "content-seo-topical-authority-auto",
        t: "Content SEO: Building Topical Authority in the Auto Vertical",
        st: "Topical Authority for Dealerships: A Content SEO Roadmap",
        sd: "How dealerships build durable topical authority in their market — from pillar pages to programmatic location coverage.",
        ex: "Topical authority is the moat that protects you from algorithm updates. Here's how to build one as a dealer.",
        k: ["topical authority dealership", "content SEO automotive", "pillar pages dealer", "programmatic SEO automotive"],
        body: [
          { type: "heading", content: "Pillar, Cluster, Programmatic" },
          { type: "paragraph", content: "A modern content strategy combines hand-crafted pillar pages, supporting cluster content, and programmatic templates that scale with inventory and geography. Each layer reinforces the others." },
          { type: "heading", content: "What to Build First" },
          { type: "bullet-list", items: [
            "One pillar per primary buyer intent: shop, finance, service, trade.",
            "Cluster content answering the long tail under each pillar.",
            "Programmatic landing pages by neighborhood, model line, and trim."
          ]},
          { type: "paragraph", content: "Topical authority compounds. Six months in, the cost per organic lead drops; twelve months in, it becomes a structural advantage." }
        ]
      }
    ]
  },
  {
    name: "Paid Search",
    img: "/images/blog-paid-search.png",
    angles: [
      {
        s: "smart-bidding-pmax-auto-dealers",
        t: "Smart Bidding for Auto: Tuning Performance Max for Dealers",
        st: "Performance Max for Car Dealers: Smart Bidding Tuning Guide",
        sd: "How dealer marketers tune Performance Max — asset groups, audience signals, and conversion priorities — for measurable lift.",
        ex: "Performance Max isn't a black box if you feed it the right signals. Here's how dealers tune it for results.",
        k: ["Performance Max dealership", "Smart Bidding auto", "Google Ads dealership", "PMax automotive"],
        body: [
          { type: "heading", content: "Signals Beat Settings" },
          { type: "paragraph", content: "Performance Max rewards the advertisers who feed it the cleanest first-party data. That means well-defined conversion values, properly weighted lead types, and audience signals built from CRM exports — not guesses." },
          { type: "heading", content: "What to Tune" },
          { type: "bullet-list", items: [
            "Differentiate conversion values: form, call, chat, appointment, sold.",
            "Feed customer match audiences for in-market and lost prospects.",
            "Use asset groups by inventory class — new, used, CPO, service.",
            "Negative keyword lists at the account level for brand safety."
          ]},
          { type: "paragraph", content: "Treat PMax like a partner, not a vending machine. The dealers who feed it well win." }
        ]
      },
      {
        s: "vehicle-listing-ads-setup-dealer",
        t: "Inventory-Aware Search Ads: Vehicle Listing Ads Setup",
        st: "Vehicle Listing Ads (VLA) Setup for Car Dealerships",
        sd: "End-to-end Vehicle Listing Ads setup — feed configuration, attributes that matter, and how to win the search results page.",
        ex: "VLAs reward inventory hygiene. Here's the setup that turns your feed into a paid search advantage.",
        k: ["Vehicle Listing Ads", "VLA setup dealership", "Google Merchant Center automotive", "inventory feed paid search"],
        body: [
          { type: "heading", content: "Feed First, Campaigns Second" },
          { type: "paragraph", content: "VLA performance lives or dies by feed quality. Trim, packages, condition, mileage, image quality — the dealers winning VLAs treat the feed as a product, not a side project." },
          { type: "heading", content: "Setup Sequence" },
          { type: "bullet-list", items: [
            "Stand up Merchant Center with the right vehicle inventory feed type.",
            "Map every required and recommended attribute, not just the basics.",
            "Add condition, mileage, and price changes as freshness signals.",
            "Wire up store codes for geo-fenced bidding by rooftop."
          ]},
          { type: "paragraph", content: "Once the feed is clean, the campaign work is straightforward — and the cost per VDP view drops." }
        ]
      },
      {
        s: "negative-keyword-hygiene-dealer-search",
        t: "Negative Keyword Hygiene for Dealer Search Campaigns",
        st: "Negative Keyword Strategy for Dealer Paid Search",
        sd: "How experienced dealer marketers build and maintain negative keyword lists that protect budget and improve quality scores.",
        ex: "The cheapest paid search optimization is the one most dealers never do — disciplined negative keyword hygiene.",
        k: ["negative keywords dealership", "paid search hygiene auto", "Google Ads negatives dealer"],
        body: [
          { type: "heading", content: "Why It's Worth It" },
          { type: "paragraph", content: "Negative keyword hygiene typically returns 10–20% of wasted spend in the first month. Yet most dealer accounts have stale lists from a vendor build six months ago." },
          { type: "heading", content: "The Working Lists" },
          { type: "bullet-list", items: [
            "Account-level: jobs, free, repair manuals, parts diagrams.",
            "Campaign-level: competitor model names where intent diverges.",
            "Ad group-level: trim and feature mismatches relative to your inventory."
          ]},
          { type: "paragraph", content: "Schedule a 30-minute weekly review. The compounding effect on CPL is real." }
        ]
      },
      {
        s: "branded-vs-conquest-paid-search-budget",
        t: "Branded vs Conquest: Allocating Paid Search Budget",
        st: "Branded vs Conquest Search: How Dealers Should Allocate Budget",
        sd: "A framework for splitting branded and conquest paid search spend at a dealership without leaving sales on the table.",
        ex: "Most dealers either over-spend on branded or starve it. Here's how to find the right split.",
        k: ["branded search dealership", "conquest search auto", "paid search budget allocation"],
        body: [
          { type: "heading", content: "Branded Is Insurance, Not Growth" },
          { type: "paragraph", content: "You should defend your branded queries, but they don't grow the store. Conquest queries — make/model and competitor terms — are where new customers come from. The split should reflect that." },
          { type: "heading", content: "A Working Framework" },
          { type: "bullet-list", items: [
            "Cap branded at the spend required to maintain top impression share.",
            "Allocate the rest by margin-weighted conversion value across conquest segments.",
            "Reserve a small test budget for new conquest themes each month."
          ]},
          { type: "paragraph", content: "Revisit the split quarterly as inventory mix and market dynamics change." }
        ]
      }
    ]
  },
  {
    name: "Paid Social",
    img: "/images/blog-paid-social.png",
    angles: [
      {
        s: "meta-inventory-ads-dealer-catalog",
        t: "Meta Inventory Ads for Car Dealers: Catalog Setup to Scale",
        st: "Meta Automotive Inventory Ads (AIA) Setup for Dealers",
        sd: "How to set up Meta Automotive Inventory Ads — catalog quality, signal flow, and the creative patterns that scale.",
        ex: "Meta AIA rewards clean catalogs and tight signal loops. Here's how to set it up right the first time.",
        k: ["Meta Automotive Inventory Ads", "AIA dealership", "Facebook catalog automotive", "Meta dynamic ads"],
        body: [
          { type: "heading", content: "Catalog Is the Engine" },
          { type: "paragraph", content: "AIA cannot work without a clean catalog. Trim accuracy, image quality, price freshness, and mileage updates flow directly into delivery and CPL. Treat the catalog like inventory itself." },
          { type: "heading", content: "Setup and Signal" },
          { type: "bullet-list", items: [
            "Create a vehicle catalog with daily refreshes and proper attribute mapping.",
            "Wire Meta Conversions API for server-side, deduped event flow.",
            "Build retargeting and prospecting ad sets at the catalog level.",
            "Use carousel and dynamic templates over hand-crafted single ads."
          ]},
          { type: "paragraph", content: "Done well, AIA is the lowest-CPL prospecting channel a dealer can run." }
        ]
      },
      {
        s: "tiktok-spark-ads-dealership-creative",
        t: "TikTok Spark Ads for Dealerships: Creative That Converts",
        st: "TikTok Spark Ads for Car Dealers: Creative Playbook",
        sd: "How to use TikTok Spark Ads at a dealership — creator partnerships, native creative patterns, and conversion expectations.",
        ex: "TikTok rewards native creative and creator partnerships. Spark Ads make both work for dealers.",
        k: ["TikTok dealership", "Spark Ads automotive", "creator marketing dealer"],
        body: [
          { type: "heading", content: "Why Spark Ads" },
          { type: "paragraph", content: "Spark Ads run from a real creator's profile, which means social proof, authentic comments, and durable engagement. For dealerships, this is the path to scale on TikTok without paying for stiff studio creative." },
          { type: "heading", content: "Creative Patterns That Work" },
          { type: "bullet-list", items: [
            "Walkaround videos shot vertically with on-screen text.",
            "Trade-in stories from real customers.",
            "Service department behind-the-scenes content.",
            "Local creator partnerships with neighborhood relevance."
          ]},
          { type: "paragraph", content: "Stop adapting TV creative for TikTok. Build it native, and let Spark Ads put paid muscle behind what's already working organically." }
        ]
      },
      {
        s: "linkedin-auto-saas-abm-playbook",
        t: "LinkedIn for Auto SaaS: ABM Playbooks That Work",
        st: "LinkedIn ABM Playbook for Automotive SaaS Vendors",
        sd: "An account-based marketing playbook for automotive SaaS companies selling into dealer groups and OEM marketing teams.",
        ex: "If you sell to dealers and OEMs, LinkedIn is the channel that actually moves pipeline. Here's how to run ABM there.",
        k: ["LinkedIn ABM auto SaaS", "automotive SaaS marketing", "dealer software marketing"],
        body: [
          { type: "heading", content: "Why LinkedIn First" },
          { type: "paragraph", content: "Decision makers at dealer groups and OEMs spend real time on LinkedIn. ABM there beats trade show booths on cost per pipeline meeting." },
          { type: "heading", content: "The Playbook" },
          { type: "bullet-list", items: [
            "Build a target account list weighted by store count and OEM coverage.",
            "Layer thought leadership ads on the buying committee, not just the buyer.",
            "Sync engagement signals into your CRM for sales prioritization.",
            "Sequence outbound and paid touches deliberately by account stage."
          ]},
          { type: "paragraph", content: "Auto SaaS is a long, multi-stakeholder sale. LinkedIn ABM is the highest-leverage channel for it." }
        ]
      },
      {
        s: "snapchat-ar-tryon-vehicle-discovery",
        t: "Snapchat AR Try-On Lenses for Vehicle Discovery",
        st: "Snapchat AR Lenses for Car Dealerships and OEMs",
        sd: "How automotive brands use Snapchat AR lenses for virtual try-ons, color variants, and showroom drive-throughs.",
        ex: "AR is no longer a gimmick — it's a discovery channel for younger automotive buyers.",
        k: ["Snapchat AR automotive", "AR lenses dealership", "virtual showroom auto"],
        body: [
          { type: "heading", content: "Why AR, Why Snap" },
          { type: "paragraph", content: "Snapchat's user base over-indexes on the buyers automotive needs to reach over the next decade. AR try-ons for color, wheel, and trim variants give them an interactive way to engage before they ever step on a lot." },
          { type: "heading", content: "What to Build" },
          { type: "bullet-list", items: [
            "Color and trim swap lenses tied to live inventory.",
            "Showroom AR walkthroughs linked to your VDPs.",
            "Trade-in evaluator lens for fast equity capture."
          ]},
          { type: "paragraph", content: "AR is a content asset, not a single campaign. Reuse the build across paid and organic." }
        ]
      }
    ]
  },
  {
    name: "Drone Video",
    img: ["/images/blog-drone-1.jpg", "/images/blog-drone-2.jpg"],
    angles: [
      {
        s: "faa-part-107-dealership-drone",
        t: "FAA Part 107 Compliance for Dealership Drone Programs",
        st: "FAA Part 107 for Car Dealership Drone Programs",
        sd: "What dealer marketing teams need to know about FAA Part 107 compliance before flying drones over their lots.",
        ex: "Before you fly a drone over your lot, get the Part 107 piece right. It's simpler than most dealers expect.",
        k: ["FAA Part 107", "dealership drone compliance", "commercial drone dealership"],
        body: [
          { type: "heading", content: "The Compliance Reality" },
          { type: "paragraph", content: "Any drone footage shot for marketing purposes is commercial use under FAA rules. That means a Part 107 certified pilot — yours or a vendor's — is required. The certification path is straightforward, but it has to actually exist." },
          { type: "heading", content: "What to Verify" },
          { type: "bullet-list", items: [
            "Pilot's Part 107 certificate is current.",
            "Airspace authorization for the dealership location.",
            "Insurance specifically covering UAS operations.",
            "Privacy considerations for adjacent properties and customers."
          ]},
          { type: "paragraph", content: "Compliance doesn't slow the production schedule when it's planned in. Skipping it does." }
        ]
      },
      {
        s: "drone-magic-hour-lot-shoots",
        t: "Drone Video Lighting: Magic Hour Shoots on the Lot",
        st: "Magic Hour Drone Video Shoots for Car Dealerships",
        sd: "Why magic hour drone shoots outperform midday footage for dealership marketing — and how to plan them.",
        ex: "The light makes the lot. Plan your drone shoots around magic hour and your conversion creative gets better, free.",
        k: ["dealership drone shoot", "magic hour drone", "automotive drone lighting"],
        body: [
          { type: "heading", content: "Light Is the Production Value" },
          { type: "paragraph", content: "A dealership lot at noon looks like a parking lot. The same lot at magic hour looks like a brand commercial. The asset cost is the same; the lighting choice is everything." },
          { type: "heading", content: "Planning Notes" },
          { type: "bullet-list", items: [
            "Schedule shoots 30 minutes before sunset, ending 15 minutes after.",
            "Pre-stage hero vehicles in pre-determined positions.",
            "Use drone follow shots, not static hovers, for energy.",
            "Capture both vertical and horizontal cuts in the same flight."
          ]},
          { type: "paragraph", content: "One magic hour shoot fuels months of paid social and VDP creative. Plan it like a campaign, not a one-off." }
        ]
      },
      {
        s: "fpv-drone-inventory-walkarounds",
        t: "Inventory Walkarounds with FPV Drones",
        st: "FPV Drone Inventory Walkarounds for Car Dealerships",
        sd: "How FPV drones unlock high-energy inventory walkarounds that static iPhone video cannot — and how to use them on VDPs.",
        ex: "FPV drone walkarounds turn a static VDP into a 30-second cinematic tour. Buyers respond, and so does Meta delivery.",
        k: ["FPV drone walkaround", "inventory video dealership", "drone walkaround"],
        body: [
          { type: "heading", content: "Why FPV" },
          { type: "paragraph", content: "FPV drones can move through doorways, around vehicles, and through interiors at speeds and angles a Steadicam cannot match. For inventory video, that's the difference between scroll-stopping and scroll-past." },
          { type: "heading", content: "Production Notes" },
          { type: "bullet-list", items: [
            "Use a small, indoor-rated FPV cinewhoop for showroom interiors.",
            "Pair drone footage with one ground-shot detail beauty pass.",
            "Edit to under 30 seconds for paid social, under 60 for VDP embeds."
          ]},
          { type: "paragraph", content: "Repeatable FPV production is the difference between buying it once and using it everywhere." }
        ]
      },
      {
        s: "drone-service-fixed-ops",
        t: "Drone Video for Service Lane and Fixed Operations",
        st: "Drone Video for Dealership Service and Fixed Ops Marketing",
        sd: "Service department marketing finally has a visual story — drone video makes the scale and capability of the service lane real.",
        ex: "Sales gets all the drone footage, but service is where margin lives. Here's how to apply drone video there.",
        k: ["service department drone", "fixed ops marketing", "service lane video"],
        body: [
          { type: "heading", content: "Show the Scale" },
          { type: "paragraph", content: "Most customers have no idea how big or capable a modern dealership service department is. Drone video shows it instantly — and that creates trust where service marketing usually struggles." },
          { type: "heading", content: "Use Cases" },
          { type: "bullet-list", items: [
            "Overhead pans of the service lane during peak hours.",
            "Behind-the-scenes shots of the parts department and detail bay.",
            "Recruiting video for technicians showing the work environment.",
            "Local awareness ads showcasing capability over price."
          ]},
          { type: "paragraph", content: "Service marketing rarely competes on price alone. Drone footage is the easiest way to compete on trust." }
        ]
      }
    ]
  },
  {
    name: "Video Marketing",
    img: "/images/blog-video-marketing.png",
    angles: [
      {
        s: "vertical-video-reels-shorts-tiktok",
        t: "Vertical Video Strategy for Reels, Shorts, and TikTok",
        st: "Vertical Video Strategy for Car Dealerships",
        sd: "A repeatable vertical video strategy across Reels, Shorts, and TikTok — built for dealership cadence and creative reuse.",
        ex: "Stop reformatting horizontal content. Build vertical-native at every step and your creative engine compounds.",
        k: ["vertical video dealership", "Reels automotive", "Shorts dealer", "TikTok strategy"],
        body: [
          { type: "heading", content: "Native Beats Reformatted" },
          { type: "paragraph", content: "Algorithms can tell the difference between a vertical-native asset and a 16:9 video squeezed into 9:16. Engagement, watch time, and CPM all reflect it. Build native or don't bother." },
          { type: "heading", content: "Cadence and Reuse" },
          { type: "bullet-list", items: [
            "One on-lot shoot day every two weeks produces 12–20 vertical assets.",
            "Reuse hooks across Reels, Shorts, and TikTok with platform-specific captions.",
            "Spark / Boost the top 10% performers based on first 48 hours of engagement."
          ]},
          { type: "paragraph", content: "Vertical video is the dominant content shape for the foreseeable future. Build the dealer creative engine around it." }
        ]
      },
      {
        s: "personal-walkarounds-bdc-conversion",
        t: "Personal Walkaround Videos: BDC and Sales Conversion",
        st: "Personal Walkaround Videos for BDC and Sales Teams",
        sd: "Personal walkaround videos from sales and BDC reps drive show rate and close rate when used in the lead lifecycle.",
        ex: "The fastest video ROI at a dealership is not a brand spot — it's a 60-second personal walkaround from a real rep.",
        k: ["personal walkaround video", "BDC video dealership", "sales conversion video"],
        body: [
          { type: "heading", content: "Why It Works" },
          { type: "paragraph", content: "Buyers buy from people. A 60-second video from the rep who took the lead changes the relationship before the buyer ever calls back. Show rate climbs, close rate climbs, time-to-close drops." },
          { type: "heading", content: "Process Notes" },
          { type: "bullet-list", items: [
            "Equip every BDC and sales rep with a simple phone tripod.",
            "Use a tool that tracks open and watch rate per video.",
            "Send within 5 minutes of lead arrival for the strongest lift.",
            "Coach delivery weekly — it's a sales skill, not a tech skill."
          ]},
          { type: "paragraph", content: "Personal video is the single highest-ROI lifecycle tactic most dealers underuse." }
        ]
      },
      {
        s: "ott-ctv-local-dealer-buys",
        t: "OTT/CTV Video Buys for Local Dealers",
        st: "OTT/CTV Advertising for Local Car Dealerships",
        sd: "How local dealers run OTT/CTV media buys that actually drive store traffic — without burning budget on impressions alone.",
        ex: "OTT and CTV are not just brand channels for dealers — when bought right, they drive measurable store visits.",
        k: ["OTT advertising dealership", "CTV ads automotive", "connected TV dealer"],
        body: [
          { type: "heading", content: "Buy It Like Performance" },
          { type: "paragraph", content: "OTT/CTV is often sold as a brand channel, but with proper measurement it produces direct response too. The trick is treating it as a performance buy with a brand bonus, not the other way around." },
          { type: "heading", content: "Setup" },
          { type: "bullet-list", items: [
            "Geofence to your trade area only — no wasted national impressions.",
            "Use multi-touch attribution that includes view-through.",
            "Sequence creative across awareness, consideration, and intent assets.",
            "Tie store visits to exposed audiences via mobile location data."
          ]},
          { type: "paragraph", content: "OTT/CTV done right delivers measurable lift, not just a nice-looking media plan." }
        ]
      },
      {
        s: "youtube-preroll-sequencing-auto-shoppers",
        t: "YouTube Pre-Roll Sequencing for Auto Shoppers",
        st: "YouTube Pre-Roll Ad Sequencing for Car Dealers",
        sd: "Sequenced YouTube pre-roll campaigns walk shoppers from intent to lot visit — here's the dealer playbook.",
        ex: "One ad doesn't sell a car. A sequence of three, served at the right moments, can.",
        k: ["YouTube pre-roll", "ad sequencing dealership", "video sequencing automotive"],
        body: [
          { type: "heading", content: "Why Sequencing" },
          { type: "paragraph", content: "Sequenced video ads tell a story across multiple touches — model intro, social proof, offer. Done well, the third ad in the sequence carries materially higher click-through than a one-shot creative." },
          { type: "heading", content: "Build the Sequence" },
          { type: "bullet-list", items: [
            "Ad 1: emotional model intro, 15 seconds.",
            "Ad 2: customer story or differentiator, 15 seconds.",
            "Ad 3: offer and CTA, 15 seconds with skippable bumper variant.",
            "Frequency capped per user across the sequence."
          ]},
          { type: "paragraph", content: "Sequencing is the highest-leverage video lever most dealers haven't pulled yet." }
        ]
      }
    ]
  },
  {
    name: "GA4 & Attribution",
    img: ["/images/blog-ga4-1.jpg", "/images/blog-attribution.png"],
    angles: [
      {
        s: "server-side-gtm-dealer-websites",
        t: "Server-Side GTM for Dealer Websites: Why and How",
        st: "Server-Side GTM for Car Dealerships: Setup and Benefits",
        sd: "Why dealer websites benefit from server-side GTM — speed, data quality, and durability against tracking changes.",
        ex: "Server-side GTM is not just a privacy upgrade — it's faster, more accurate, and more durable than client-side alone.",
        k: ["server-side GTM", "dealer GTM setup", "Google Tag Manager automotive"],
        body: [
          { type: "heading", content: "Why It Matters Now" },
          { type: "paragraph", content: "Browser-based tracking keeps shrinking. Server-side GTM moves the critical event flow off the browser, improving page speed, data quality, and resilience to ad-blockers and ITP changes." },
          { type: "heading", content: "Implementation Steps" },
          { type: "bullet-list", items: [
            "Stand up a tagging server on a first-party subdomain.",
            "Migrate Meta CAPI, GA4, and Google Ads conversion flows server-side.",
            "Maintain client-side GTM for events that genuinely need it.",
            "Validate every tag with debug mode before cutover."
          ]},
          { type: "paragraph", content: "The dealers who get this right protect attribution as the wider tracking landscape continues to shift." }
        ]
      },
      {
        s: "multi-touch-attribution-auto-buyer-journey",
        t: "Multi-Touch Attribution Models for the Auto Buyer Journey",
        st: "Multi-Touch Attribution for Car Dealerships",
        sd: "How to choose and implement a multi-touch attribution model that actually fits the long, multi-channel auto buyer journey.",
        ex: "Last-click attribution lies to dealers. Pick a multi-touch model that reflects how cars actually get bought.",
        k: ["multi-touch attribution dealership", "MTA automotive", "buyer journey attribution"],
        body: [
          { type: "heading", content: "Why Last-Click Lies" },
          { type: "paragraph", content: "Auto buyers research for weeks across organic, paid, social, video, and direct channels. Crediting only the last click rewards branded search and direct — channels that already would have closed." },
          { type: "heading", content: "Choosing a Model" },
          { type: "bullet-list", items: [
            "Data-driven attribution where you have enough conversion volume.",
            "Position-based (40/20/40) as a sane manual default.",
            "Time-decay if your sales cycle is short and inventory-driven.",
            "Always validate the model against incrementality tests."
          ]},
          { type: "paragraph", content: "The right model is the one your team will actually act on. Pick something defensible and run it consistently." }
        ]
      },
      {
        s: "offline-sales-dms-ga4-enhanced-conversions",
        t: "Connecting Offline Sales (DMS) to GA4 with Enhanced Conversions",
        st: "Connect DMS Sales to GA4 With Enhanced Conversions",
        sd: "How to connect offline DMS sales data back into GA4 and Google Ads using enhanced conversions for the dealer use case.",
        ex: "If your sold deals don't flow back into GA4, your bidding algorithms are training on the wrong outcome.",
        k: ["enhanced conversions dealership", "DMS GA4 integration", "offline conversion tracking auto"],
        body: [
          { type: "heading", content: "Close the Loop" },
          { type: "paragraph", content: "A lead is not a sale. If GA4 and Google Ads only see leads, your bidding optimizes for leads — not sold units. Wiring DMS data back into your conversion stack changes which audiences and channels actually win budget." },
          { type: "heading", content: "Implementation Outline" },
          { type: "bullet-list", items: [
            "Pipe sold deals out of the DMS on a daily schedule.",
            "Match by hashed email and phone to original GA4 client IDs.",
            "Send sold conversions back via measurement protocol or offline conversion import.",
            "Re-train bidding strategies on the new conversion definition."
          ]},
          { type: "paragraph", content: "Closed-loop attribution is the highest-impact GA4 project most dealers haven't completed." }
        ]
      },
      {
        s: "audience-building-ga4-auto-remarketing",
        t: "Audience Building in GA4 for Auto Remarketing",
        st: "GA4 Audience Building for Dealership Remarketing",
        sd: "How to design GA4 audiences that drive better dealership remarketing — VDP viewers, calculator users, and lifecycle stages.",
        ex: "Default GA4 audiences are useless for dealers. Build the ones that actually map to the buyer journey.",
        k: ["GA4 audiences", "dealership remarketing", "automotive audience building"],
        body: [
          { type: "heading", content: "Build for Intent" },
          { type: "paragraph", content: "Audience design is where remarketing wins or loses. Generic 'all visitors' audiences are wasted spend. The dealers who win build audiences for specific buyer behaviors and lifecycle stages." },
          { type: "heading", content: "Audiences Worth Building" },
          { type: "bullet-list", items: [
            "Multi-VDP viewers with no form submit in the last 14 days.",
            "Trade calculator engagers without lead form complete.",
            "Service appointment cancellers in the last 30 days.",
            "Past customers approaching equity by months-since-sold."
          ]},
          { type: "paragraph", content: "Push these audiences to Google Ads and Meta, and let the bidding work for you." }
        ]
      }
    ]
  },
  {
    name: "Dealer Marketing",
    img: ["/images/blog-oem.jpg", "/images/blog-dealership.png"],
    angles: [
      {
        s: "oem-tier-coordination-channel-conflict",
        t: "OEM Tier 1/2/3 Coordination Without Channel Conflict",
        st: "OEM Tier 1, 2, 3 Coordination for Dealerships",
        sd: "How dealer marketers coordinate Tier 1, 2, and 3 spend without cannibalizing themselves — and where the leverage really sits.",
        ex: "Tier conflict is the silent budget tax most dealer groups pay. Here's how to fix it.",
        k: ["OEM tier marketing", "tier 2 dealership", "tier 3 dealer marketing"],
        body: [
          { type: "heading", content: "The Conflict" },
          { type: "paragraph", content: "Tier 1 (OEM), Tier 2 (regional), and Tier 3 (dealer) are all bidding on the same intent. When uncoordinated, the dealer pays the highest cost and gets the worst share of voice." },
          { type: "heading", content: "What Coordination Looks Like" },
          { type: "bullet-list", items: [
            "Map keyword and audience overlaps across the three tiers monthly.",
            "Establish a clear Tier 3 lane focused on inventory and local intent.",
            "Negotiate co-op alignment with regional reps quarterly.",
            "Use brand search intent to reduce, not duplicate, OEM spend."
          ]},
          { type: "paragraph", content: "Coordinated tiering compounds. Run it as a process, not a one-time review." }
        ]
      },
      {
        s: "service-marketing-ro-lifecycle",
        t: "Service Marketing: Driving RO Count With Lifecycle Campaigns",
        st: "Service Marketing for Dealerships: Lifecycle Playbook",
        sd: "How dealer marketing teams drive RO count with lifecycle service campaigns instead of price-and-discount blasts.",
        ex: "Service marketing isn't a coupon. Build lifecycle campaigns and RO count climbs without margin damage.",
        k: ["service marketing dealership", "RO count lift", "service lifecycle campaigns"],
        body: [
          { type: "heading", content: "Stop Discounting" },
          { type: "paragraph", content: "Discount-only service marketing trains customers to wait for the next coupon. Lifecycle campaigns map service messages to vehicle ownership stages — and they drive RO count without crashing margin." },
          { type: "heading", content: "Lifecycle Touches" },
          { type: "bullet-list", items: [
            "First service reminder anchored to delivery date.",
            "Manufacturer-recommended service triggers by mileage.",
            "Seasonal touches: tires, batteries, AC, cooling.",
            "Win-back sequences for customers who serviced once and lapsed."
          ]},
          { type: "paragraph", content: "Treat service marketing as a year-long program, not a coupon calendar." }
        ]
      },
      {
        s: "used-car-marketing-playbook",
        t: "Used Car Marketing Playbook for Independent and Franchise Dealers",
        st: "Used Car Marketing Playbook for Dealerships",
        sd: "A used car marketing playbook covering merchandising, paid, organic, and lifecycle for franchise and independent dealers.",
        ex: "Used moves faster than new and reacts harder to merchandising. This is the playbook that wins.",
        k: ["used car marketing", "pre-owned marketing dealership", "used vehicle SEO"],
        body: [
          { type: "heading", content: "Merchandising Is Marketing" },
          { type: "paragraph", content: "On used inventory, photo quality, vehicle description, and time-to-online are marketing levers, not back-office tasks. The first 48 hours after a unit is in stock are where most of the price elasticity lives." },
          { type: "heading", content: "Channels and Cadence" },
          { type: "bullet-list", items: [
            "Same-day photography and VDP publish for every used unit.",
            "VLAs and Meta AIA wired to the used catalog.",
            "Aged inventory triggers automated price-drop email sequences.",
            "SEO templates for high-volume year/make/model used queries."
          ]},
          { type: "paragraph", content: "Used is a velocity game. The marketing engine has to match it." }
        ]
      },
      {
        s: "grand-opening-marketing-new-dealership",
        t: "Grand Opening Marketing for New Dealership Locations",
        st: "Grand Opening Marketing Plan for New Dealerships",
        sd: "How to plan grand opening marketing for a new dealership location — from local PR to launch-week paid pushes.",
        ex: "Grand opening week sets the curve for the next 12 months. Here's how to build the launch plan.",
        k: ["dealership grand opening", "new dealership marketing", "launch marketing dealer"],
        body: [
          { type: "heading", content: "Treat It Like a Launch" },
          { type: "paragraph", content: "A new dealership location is a brand launch in a new market. The first three months should be planned with the same intensity a SaaS company plans a product launch." },
          { type: "heading", content: "Launch Plan Components" },
          { type: "bullet-list", items: [
            "Pre-launch: Google Business Profile, citations, and local PR seeding.",
            "Launch week: paid social, OTT/CTV, local print/radio if relevant.",
            "Owned events: ribbon-cutting, community partnerships, charity tie-ins.",
            "Post-launch: 90-day measurement and iteration."
          ]},
          { type: "paragraph", content: "Most grand opening plans peak at week one and trail off. The good ones run a full 90-day arc." }
        ]
      }
    ]
  },
  {
    name: "CRM",
    img: "/images/blog-crm.png",
    angles: [
      {
        s: "elead-crm-process-show-rate",
        t: "eLead CRM: Process Automation That Lifts Show Rate",
        st: "eLead CRM: Appointment Follow-Up & No-Show Recovery",
        sd: "Explore appointment confirmations, no-show recovery, and follow-up workflows in eLead CRM. Give your dealership team a more consistent customer process.",
        ex: "eLead has more leverage than most dealers use. The right automation lifts show rate without adding headcount.",
        k: ["eLead CRM", "eLead automation", "dealership show rate", "eLead workflows"],
        body: [
          { type: "heading", content: "What Most Stores Miss" },
          { type: "paragraph", content: "Most eLead implementations stop at templates and call cadence. The real lift is in process automation: appointment confirmations, no-show recovery, and equity triggers fired on the right day, every day." },
          { type: "heading", content: "Workflows Worth Building" },
          { type: "bullet-list", items: [
            "Inbound lead first-touch automation under five minutes.",
            "Appointment confirmation 24 and 2 hours before set time.",
            "No-show recovery sequence with two outbound touches and an offer.",
            "Equity-triggered conquest sequences for the existing book."
          ]},
          { type: "paragraph", content: "Done well, eLead automation is one of the highest-ROI improvements a single-point store can make." }
        ]
      },
      {
        s: "vinsolutions-connect-reports-action",
        t: "VinSolutions Connect: Reports That Actually Drive Action",
        st: "VinSolutions Connect: Reports Dealers Should Run Weekly",
        sd: "The VinSolutions Connect reports that actually move show rate, close rate, and gross — and how to operationalize them.",
        ex: "VinSolutions has hundreds of reports. These are the handful that actually change behavior at a store.",
        k: ["VinSolutions Connect", "VinSolutions reports", "dealer CRM reporting"],
        body: [
          { type: "heading", content: "Reports vs Action" },
          { type: "paragraph", content: "A report nobody acts on is just data exhaust. The reports that change store performance are the ones tied to a Monday morning conversation and a Friday accountability check." },
          { type: "heading", content: "Weekly Reports That Matter" },
          { type: "bullet-list", items: [
            "Lead source ROI by sold unit, not just delivered lead count.",
            "BDC contact rate and appointment set rate by rep.",
            "Sales rep close rate by source and unit type.",
            "Aged opportunity working list for managers."
          ]},
          { type: "paragraph", content: "Less reporting, more action. Anchor each report to one decision and keep it on the calendar." }
        ]
      },
      {
        s: "tekion-crm-data-architecture",
        t: "Tekion CRM: Data Architecture for Modern Dealerships",
        st: "Tekion CRM and Data Architecture for Dealerships",
        sd: "How modern dealer groups structure data architecture around Tekion CRM and DMS to unlock real marketing and ops leverage.",
        ex: "Tekion changes what's possible at a dealership. Here's the data architecture that makes the upside real.",
        k: ["Tekion CRM", "Tekion DMS", "dealer data architecture", "automotive data platform"],
        body: [
          { type: "heading", content: "Why Architecture Matters" },
          { type: "paragraph", content: "Tekion's value isn't the UI — it's the unified data layer underneath. Dealers who plan for that get marketing, sales, and ops leverage. Dealers who treat it like 'just another CRM' miss it." },
          { type: "heading", content: "Architecture Patterns" },
          { type: "bullet-list", items: [
            "Single customer ID across CRM, DMS, and digital.",
            "Real-time inventory feeds to digital from the system of record.",
            "Closed-loop sales attribution back to original digital source.",
            "Service customer lifecycle modeling on the unified data layer."
          ]},
          { type: "paragraph", content: "The dealers who plan the data layer first get most of the platform's promised upside." }
        ]
      },
      {
        s: "crm-migration-customer-history",
        t: "CRM Migration Without Losing Customer History",
        st: "CRM Migration for Dealerships Without Losing Customer Data",
        sd: "A practical playbook for migrating dealer CRMs (eLead, VinSolutions, Tekion, others) without losing customer history or pipeline.",
        ex: "CRM migrations are where customer data goes to die. Here's the playbook that prevents it.",
        k: ["CRM migration dealership", "dealer CRM switch", "customer history migration"],
        body: [
          { type: "heading", content: "What Goes Wrong" },
          { type: "paragraph", content: "Most CRM migrations underestimate the data work and overestimate the new platform's onboarding capacity. The result is fragmented history, broken automation, and frustrated salespeople." },
          { type: "heading", content: "The Migration Playbook" },
          { type: "bullet-list", items: [
            "Audit and clean the source data before exporting.",
            "Map fields and merge duplicate records by hashed identifiers.",
            "Pilot with one rooftop or one team before the full cutover.",
            "Run both systems in parallel for two billing cycles."
          ]},
          { type: "paragraph", content: "A clean migration takes longer and costs more than the demo suggested. Plan for it, and the new platform actually pays off." }
        ]
      }
    ]
  },
  {
    name: "Email",
    img: "/images/blog-email.png",
    angles: [
      {
        s: "dealer-email-deliverability-warm-up",
        t: "Dealer Email Deliverability: Warming Up a New IP",
        st: "Email Deliverability for Car Dealerships: IP Warmup Guide",
        sd: "How to warm up a new sending IP for dealer email programs without burning your domain reputation.",
        ex: "Email deliverability is fragile and hard-won. Treat IP warmup like a launch, not an afterthought.",
        k: ["email deliverability dealership", "IP warmup", "dealer email reputation"],
        body: [
          { type: "heading", content: "Why It Goes Wrong" },
          { type: "paragraph", content: "Most dealer email programs are run from one or two sending IPs. When those IPs go cold or get switched without a proper warmup, deliverability drops and so does revenue." },
          { type: "heading", content: "Warmup Plan" },
          { type: "bullet-list", items: [
            "Authenticate with SPF, DKIM, and DMARC before any send.",
            "Start with the most engaged 5% of the list, then ramp by 25% per week.",
            "Suppress hard bounces and unengaged segments aggressively early.",
            "Monitor deliverability dashboards daily for the first 30 days."
          ]},
          { type: "paragraph", content: "Warmup is six weeks of patience for a year of clean inbox placement." }
        ]
      },
      {
        s: "equity-mining-email-campaigns",
        t: "Equity Mining Email Campaigns That Convert",
        st: "Equity Mining Email Campaigns for Car Dealerships",
        sd: "Equity mining email campaigns convert when the data is fresh and the offer is real — here's how to run them for a dealership.",
        ex: "Equity mining lives or dies on data freshness and offer specificity. Generic blasts don't move the needle.",
        k: ["equity mining email", "dealer email campaigns", "automotive equity mining"],
        body: [
          { type: "heading", content: "What Real Equity Mining Looks Like" },
          { type: "paragraph", content: "An equity mining email is not a generic 'we'll buy your trade' blast. It's a specific equity position, a specific replacement vehicle, and a specific monthly impact — calculated for that customer." },
          { type: "heading", content: "Operational Notes" },
          { type: "bullet-list", items: [
            "Refresh equity data weekly from the DMS, not monthly.",
            "Use real comparable replacement units in stock.",
            "Personalize subject and preheader with vehicle and equity figures.",
            "Trigger to BDC for outbound on opens and clicks."
          ]},
          { type: "paragraph", content: "Done right, equity mining is one of the most profitable email programs at a dealership." }
        ]
      },
      {
        s: "service-reminder-sequences-cadence",
        t: "Service Reminder Sequences: Frequency and Cadence",
        st: "Service Reminder Email Sequences for Car Dealerships",
        sd: "How to set service reminder email frequency and cadence so dealers maximize RO count without burning the list.",
        ex: "Send too often and the list opts out. Send too rarely and they go to the quick lube. Get the cadence right.",
        k: ["service reminder email", "service email cadence", "RO email dealership"],
        body: [
          { type: "heading", content: "Find the Right Frequency" },
          { type: "paragraph", content: "Service reminder cadence depends on vehicle age, service history, and OEM-recommended intervals. A one-size-fits-all monthly blast fatigues the list and undersells the message." },
          { type: "heading", content: "Cadence Pattern" },
          { type: "bullet-list", items: [
            "Trigger sends on mileage or months-since-last-service, not the calendar.",
            "Cap to no more than two service messages in any 30 days.",
            "Build distinct sequences for OEM service vs general maintenance.",
            "Suppress the list during scheduled appointments to avoid double-touching."
          ]},
          { type: "paragraph", content: "Smart cadence keeps service emails welcome instead of resented." }
        ]
      },
      {
        s: "email-segmentation-beyond-buy-lease-service",
        t: "Email Segmentation Beyond Buy/Lease/Service",
        st: "Email Segmentation Strategy for Car Dealerships",
        sd: "Modern dealer email segmentation goes beyond buy/lease/service — here are the segments that actually drive open rate and revenue.",
        ex: "Three-way segmentation isn't enough. Build the segments that match how customers actually relate to your store.",
        k: ["email segmentation dealership", "automotive email segments", "dealer email strategy"],
        body: [
          { type: "heading", content: "Old Segments, New Segments" },
          { type: "paragraph", content: "The old buy/lease/service split is too coarse to drive performance. Modern email segmentation layers behavior, lifecycle, and value to send the right message to the right inbox." },
          { type: "heading", content: "Segments Worth Building" },
          { type: "bullet-list", items: [
            "Past customers by months-since-sold and equity position.",
            "Service-only customers with no sales relationship yet.",
            "Engaged-but-unconverted leads from the last 60 days.",
            "VIPs and high-LTV customers worth dedicated touches."
          ]},
          { type: "paragraph", content: "Better segmentation pays for itself in open rate and revenue per send." }
        ]
      }
    ]
  },
  {
    name: "Vendor Management",
    img: "/images/blog-vendor-management.png",
    angles: [
      {
        s: "audit-dealer-marketing-vendor-stack",
        t: "How to Audit Your Dealer Marketing Vendor Stack",
        st: "Audit Your Dealership Marketing Vendor Stack",
        sd: "A practical framework for auditing your dealership marketing vendor stack — overlap, performance, and contract leverage.",
        ex: "Most dealers cannot list every vendor they pay. The audit alone usually finds 10–20% of recoverable spend.",
        k: ["dealer marketing vendor audit", "vendor stack audit", "dealership marketing review"],
        body: [
          { type: "heading", content: "What the Audit Reveals" },
          { type: "paragraph", content: "An honest vendor audit usually surfaces overlapping tools, retainers paying for work nobody uses, and contracts that auto-renewed without anyone noticing. The first cleanup typically returns 10–20% of marketing spend." },
          { type: "heading", content: "Audit Framework" },
          { type: "bullet-list", items: [
            "List every vendor, monthly cost, contract end date, and primary owner.",
            "Map vendors to outcomes — leads, traffic, branding, ops support.",
            "Score performance against contracted SLAs and last 90 days of data.",
            "Flag overlap, underperformance, and missing accountability."
          ]},
          { type: "paragraph", content: "Run the audit annually. The compounding savings and clarity are real." }
        ]
      },
      {
        s: "replacing-vendors-without-breaking-leads",
        t: "Replacing Underperforming Vendors Without Breaking Lead Flow",
        st: "Replace Dealership Marketing Vendors Without Breaking Leads",
        sd: "How to swap underperforming dealer marketing vendors without dropping lead flow during the transition.",
        ex: "The fear of breaking lead flow is what keeps dealers stuck with bad vendors. Here's how to switch safely.",
        k: ["replace dealer vendor", "vendor switch dealership", "marketing vendor transition"],
        body: [
          { type: "heading", content: "Why Dealers Stay Stuck" },
          { type: "paragraph", content: "Vendor switches feel risky because the alternative is unknown and the current performance is at least known. But staying with a bad vendor compounds — the cost of staying is usually higher than the risk of switching." },
          { type: "heading", content: "Safe Switching Pattern" },
          { type: "bullet-list", items: [
            "Run new and outgoing vendor in parallel for 30–60 days.",
            "Compare lead volume, lead quality, and CPL daily during overlap.",
            "Migrate tracking and assets before paid budget moves.",
            "Document everything for the next switch."
          ]},
          { type: "paragraph", content: "A clean transition is process, not magic. Most failures are calendar problems, not capability problems." }
        ]
      },
      {
        s: "negotiating-dealer-marketing-contracts",
        t: "Negotiating Dealer Marketing Contracts: SLAs and Outs",
        st: "Negotiate Dealer Marketing Contracts (SLAs and Outs)",
        sd: "How to negotiate dealer marketing contracts so SLAs are real, performance triggers exist, and the out clauses actually work.",
        ex: "Most dealer marketing contracts protect the vendor. These are the clauses that should protect the store too.",
        k: ["dealer marketing contracts", "vendor SLA dealership", "marketing contract negotiation"],
        body: [
          { type: "heading", content: "Read the Contract You Signed" },
          { type: "paragraph", content: "Most dealer contracts auto-renew, lock pricing, and have weak performance language. The renewal moment is when leverage exists — use it." },
          { type: "heading", content: "Clauses Worth Fighting For" },
          { type: "bullet-list", items: [
            "Performance SLAs with measurable, mutually owned definitions.",
            "Performance-trigger termination rights, not just convenience outs.",
            "Data ownership and export rights at termination.",
            "Quarterly business reviews with documented action items."
          ]},
          { type: "paragraph", content: "Better contracts produce better vendor relationships. The vendors who balk are the ones telling you something." }
        ]
      },
      {
        s: "true-cost-vendor-sprawl-dealer-marketing",
        t: "The True Cost of Vendor Sprawl in Dealer Marketing",
        st: "True Cost of Vendor Sprawl in Dealership Marketing",
        sd: "Vendor sprawl quietly inflates cost and degrades performance. Here's how to measure it and what to do about it.",
        ex: "Vendor sprawl is the silent leak in most dealer marketing budgets. Measuring it is the first step to plugging it.",
        k: ["vendor sprawl dealership", "dealer vendor consolidation", "marketing vendor cost"],
        body: [
          { type: "heading", content: "Sprawl as a Cost Center" },
          { type: "paragraph", content: "Each new vendor adds a contract, an integration, an onboarding, a reporting overlap, and an internal owner who has to coordinate. The real cost is rarely on the invoice." },
          { type: "heading", content: "Measuring and Reducing It" },
          { type: "bullet-list", items: [
            "Inventory all vendors and the staff time each consumes monthly.",
            "Identify duplicated capabilities across two or more vendors.",
            "Consolidate where one strong vendor replaces two average ones.",
            "Reinvest the recovered budget into measurement and creative."
          ]},
          { type: "paragraph", content: "Less sprawl, more leverage. The dealers who run a tight stack outperform." }
        ]
      }
    ]
  },
  {
    name: "Fractional CMO",
    img: "/images/blog-fractional-cmo.png",
    angles: [
      {
        s: "fractional-cmo-vs-fulltime-auto",
        t: "Fractional CMO vs Full-Time: Cost Math for Auto",
        st: "Fractional CMO vs Full-Time CMO: Auto Industry Math",
        sd: "The real cost-benefit math of a fractional CMO vs a full-time CMO for dealer groups, single-point stores, and auto SaaS.",
        ex: "The fractional vs full-time question isn't ideological — it's math. Here's the math for the auto industry.",
        k: ["fractional CMO", "full-time CMO", "automotive CMO cost"],
        body: [
          { type: "heading", content: "What You're Really Paying For" },
          { type: "paragraph", content: "A full-time auto CMO at the right level often costs 350–500k all-in. A fractional CMO delivers the strategic and operational layer at a fraction of that cost — but it has to be the right size for the operation." },
          { type: "heading", content: "When Fractional Wins" },
          { type: "bullet-list", items: [
            "Single-point stores and small groups under 5 rooftops.",
            "Auto SaaS pre-Series B without a full marketing team built yet.",
            "Companies in transition that need leadership but not headcount.",
            "Operators who already have execution in place but need strategy."
          ]},
          { type: "paragraph", content: "When the math says fractional, the operational result usually beats the full-time alternative." }
        ]
      },
      {
        s: "fractional-cmo-onboarding-90-days",
        t: "Onboarding a Fractional CMO: First 90 Days",
        st: "Onboarding a Fractional CMO at a Dealership: 90-Day Plan",
        sd: "What the first 90 days of a fractional CMO engagement should look like at a dealer group, single-point store, or auto SaaS.",
        ex: "The first 90 days set the trajectory of the engagement. Here's how to make them count.",
        k: ["fractional CMO onboarding", "CMO 90 day plan", "automotive marketing leadership"],
        body: [
          { type: "heading", content: "Three Phases" },
          { type: "paragraph", content: "Days 1–30 are diagnosis. Days 31–60 are quick wins and prioritization. Days 61–90 are operational rhythm and a 12-month roadmap. Skip a phase and the engagement stalls." },
          { type: "heading", content: "Day-By-Day Pattern" },
          { type: "bullet-list", items: [
            "Week 1–2: data, vendor, and people inventory.",
            "Week 3–4: gap analysis and first quick wins.",
            "Week 5–8: rebuild measurement and ship two campaign improvements.",
            "Week 9–12: roadmap, hiring plan if needed, and operating cadence."
          ]},
          { type: "paragraph", content: "Onboarding is the highest-leverage period of any fractional engagement. Plan it deliberately." }
        ]
      },
      {
        s: "single-point-store-needs-marketing-leadership",
        t: "When Single-Point Stores Need Marketing Leadership",
        st: "When a Single-Point Store Needs a Fractional CMO",
        sd: "Signals that a single-point dealership has outgrown vendor management and needs a real marketing leader at the table.",
        ex: "Vendor managers and marketing leaders are different jobs. Here's when a single-point store needs the latter.",
        k: ["single point dealership", "dealer marketing leadership", "fractional CMO single store"],
        body: [
          { type: "heading", content: "The Inflection Point" },
          { type: "paragraph", content: "Most single-point stores grow up with a marketing manager who is, in practice, a vendor manager. That's enough up to a point. Past that point, the lack of leadership shows up as flat traffic, vendor sprawl, and unclear strategy." },
          { type: "heading", content: "Signals to Watch" },
          { type: "bullet-list", items: [
            "Same vendors for 3+ years with diminishing return.",
            "No clear marketing strategy beyond OEM compliance.",
            "Decisions made by the vendor, not the store.",
            "GM frustration about marketing without a clear remedy."
          ]},
          { type: "paragraph", content: "When the signals show up, a fractional CMO usually unlocks the next stage of growth." }
        ]
      },
      {
        s: "fractional-cmo-roi-measurement",
        t: "Fractional CMO ROI: Measuring the Strategic Hire",
        st: "Measuring Fractional CMO ROI in Automotive",
        sd: "How to measure the ROI of a fractional CMO engagement at a dealership or auto SaaS company — beyond vanity metrics.",
        ex: "Strategic hires are still measurable. Here's how to evaluate fractional CMO ROI without slipping into vibes.",
        k: ["fractional CMO ROI", "marketing leader ROI", "automotive CMO measurement"],
        body: [
          { type: "heading", content: "Don't Confuse Output and Outcome" },
          { type: "paragraph", content: "A fractional CMO that ships 50 PowerPoint decks but doesn't move sold units, leads, or pipeline isn't producing ROI. Outcome metrics — not output — should anchor the relationship." },
          { type: "heading", content: "Measuring the Engagement" },
          { type: "bullet-list", items: [
            "Define outcome KPIs at the start: leads, sold units, pipeline, retention.",
            "Track quarterly improvement against a credible baseline.",
            "Tie compensation or scope to outcomes where it makes sense.",
            "Re-evaluate fit at 6 and 12 months on outcome, not vibes."
          ]},
          { type: "paragraph", content: "A fractional CMO is a strategic hire. Measure it like one." }
        ]
      }
    ]
  }
];

function pickImage(img: string | string[], index: number): string {
  return Array.isArray(img) ? img[index % img.length] : img;
}

function generatePosts(): BlogPost[] {
  const posts: BlogPost[] = [];
  const start = new Date("2021-06-07T00:00:00Z");
  let i = 0;
  let d = new Date(start);
  // Emit exactly one evergreen post per angle (cycle 0 only). Date-stamped
  // update variants reuse the same body verbatim, creating duplicate-content
  // URLs that dilute ranking signals, waste crawl budget, and risk keyword
  // cannibalization. One canonical URL per angle is the correct SEO posture.
  const totalAngles = topics.reduce((n, t) => n + t.angles.length, 0);
  while (i < totalAngles) {
    const topic = topics[i % topics.length];
    const angleCycle = Math.floor(i / topics.length);
    const angle = topic.angles[angleCycle % topic.angles.length];
    const isoDate = d.toISOString().slice(0, 10);
    const heroImage = pickImage(topic.img, i);
    posts.push({
      slug: angle.s,
      title: angle.t,
      seoTitle: angle.st,
      seoDescription: angle.sd,
      keywords: angle.k,
      excerpt: angle.ex,
      category: topic.name,
      publishedDate: isoDate,
      heroImage,
      body: angle.body,
    });
    d = new Date(d.getTime() + 7 * 24 * 60 * 60 * 1000);
    i++;
  }
  return posts;
}

const generated = generatePosts();
const seen = new Set(featured.map((p) => p.slug));
const generatedUnique = generated.filter((p) => !seen.has(p.slug));

function finalizeExistingPost(p: BlogPost): BlogPost {
  return {
    ...p,
    author: p.author ?? DEFAULT_AUTHOR,
    body: [...p.body, ...expandBody(p)],
  };
}

function finalizeScheduledPost(p: BlogPost): BlogPost {
  // Scheduled posts are complete, original articles supplied by writers. Do
  // not append the legacy synthetic expansion used by the older generated
  // library; doing so would change their editorial body at publish time.
  return {
    ...p,
    author: p.author ?? DEFAULT_AUTHOR,
    body: [...p.body],
  };
}

const scheduledPosts = [
  ...scheduledPart1,
  ...scheduledPart2,
  ...scheduledPart3,
  ...scheduledPart4,
  ...scheduledPart5,
  ...scheduledPart6,
];

export const blogPosts: BlogPost[] = [...featured, ...generatedUnique]
  .map(finalizeExistingPost)
  .concat(scheduledPosts.map(finalizeScheduledPost))
  .concat([
    ...backfillPart1, ...backfillPart2, ...backfillPart3,
    ...backfillPart4, ...backfillPart5, ...backfillPart6,
  ].map(finalizeScheduledPost))
  .sort((a, b) => b.publishedDate.localeCompare(a.publishedDate));

const seenSlugs = new Set<string>();
for (const post of blogPosts) {
  if (seenSlugs.has(post.slug)) {
    throw new Error(`[blog] Duplicate post slug: "${post.slug}"`);
  }
  seenSlugs.add(post.slug);
}

function utcDate(now: Date | string): string {
  const parsed =
    typeof now === "string"
      ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(now) ? `${now}T00:00:00Z` : now)
      : now;
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`[blog] Invalid publication clock: "${String(now)}"`);
  }
  return parsed.toISOString().slice(0, 10);
}

function defaultPublicationClock(): Date | string {
  // This flag is injected only into the isolated Chromium page used by
  // scripts/prerender.ts. Production browser routing always evaluates UTC
  // wall-clock time, and the HTTP server independently enforces the same gate.
  if (
    typeof window !== "undefined" &&
    (window as Window & { __RD_PRERENDER_ALL_BLOG_POSTS__?: boolean })
      .__RD_PRERENDER_ALL_BLOG_POSTS__
  ) {
    return "9999-12-31";
  }
  return new Date();
}

export function getAllPosts(): BlogPost[] {
  return blogPosts;
}

/**
 * A small, explicit compatibility rule for the former date-stamped generated
 * URLs. It only resolves when the retained undated slug exists, so guessed
 * slugs never become redirects.
 */
export function getCanonicalBlogSlug(slug: string): string | undefined {
  if (seenSlugs.has(slug)) return slug;
  const match = slug.match(/^(.+)-\d{4}-\d{2}-\d{2}$/);
  return match && seenSlugs.has(match[1]) ? match[1] : undefined;
}

export function getPublishedPosts(now: Date | string = defaultPublicationClock()): BlogPost[] {
  const today = utcDate(now);
  return blogPosts.filter((p) => p.publishedDate <= today);
}

export function getPostBySlug(
  slug: string,
  now: Date | string = defaultPublicationClock(),
): BlogPost | undefined {
  const today = utcDate(now);
  const post = blogPosts.find((p) => p.slug === slug);
  if (!post) return undefined;
  if (post.publishedDate > today) return undefined;
  return post;
}
